import type { ReactNode } from 'react';
import { Clock, User } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ProgressBar from '../ui/ProgressBar';
import './CourseCard.css';

export type CourseCardCourse = {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    level: 'Beginner' | 'Intermediate' | 'Advanced';
    category: string;
    thumbnail: string;
};

export type CourseCardProps = {
    course: CourseCardCourse;
    progress: number;
    thumbnailIcon: ReactNode;
    onOpen: () => void;
};

function CourseCard({ course, progress, thumbnailIcon, onOpen }: CourseCardProps) {
    const cta =
        progress === 0 ? 'Start Course' : progress === 100 ? 'Review' : 'Continue';

    return (
        <Card variant="stacked" className="course-card-shell">
            <Card.Media className="course-card__media">
                <div className="course-card__thumbnail">{thumbnailIcon}</div>
            </Card.Media>
            <Card.Header>
                <div className="course-card__heading">
                    <div className="course-card__meta">
                        <span
                            className={`course-card__level course-card__level--${course.level.toLowerCase()}`}
                        >
                            {course.level}
                        </span>
                        <span className="course-card__category">{course.category}</span>
                    </div>
                    <h3 className="course-card__title">{course.title}</h3>
                </div>
            </Card.Header>
            <Card.Body>
                <p className="course-card__description">{course.description}</p>
                <div className="course-card__info">
                    <span className="course-card__info-item">
                        <User size={14} />
                        {course.instructor}
                    </span>
                    <span className="course-card__info-item">
                        <Clock size={14} />
                        {course.duration}
                    </span>
                </div>
                {progress > 0 && (
                    <ProgressBar
                        value={progress}
                        showLabel
                        ariaLabel={`${course.title} progress`}
                    />
                )}
            </Card.Body>
            <Card.Footer>
                <Button variant="primary" size="md" fullWidth onClick={onOpen}>
                    {cta}
                </Button>
            </Card.Footer>
        </Card>
    );
}

export default CourseCard;
